import React from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import ConstituentSearchFilterField from '@/constants/ConstituentSearchFilterField';
import FilterOperator from '@/constants/forms/FilterOperator';
import useConstituent from '@/lib/hooks/swr/useConstituent';
import useCustomFields from '@/lib/hooks/swr/useCustomFields';
import usePreselectedLabels from '@/lib/hooks/swr/usePreselectedLabels';
import {
  IWaveFilterDefaultValues,
  useIWaveIntegration,
} from '@/modules/IWaveIntegration';
import { useQueryProvider } from '@/modules/QueryProvider';
import type AdvancedConstituentSearchFilter from '@/types/AdvancedConstituentSearchFilter';
import type Constituent from '@/types/api/Constituent';
import ConstituentSearchDefaultValues from '../constants/ConstituentSearchDefaultValues';
import TemporaryFilterField from '../constants/TemporaryFilterField';
import type ConstituentSearchFilterParams from '../types/ConstituentSearchFilterParams';
import mergeDefaultValues from '../utils/mergeDefaultValues';

function ConstituentSearchFormProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const { query } = useQueryProvider<ConstituentSearchFilterParams>();
  const iWaveIntegration = useIWaveIntegration();

  const queryFilters: AdvancedConstituentSearchFilter[] = React.useMemo(
    () =>
      'filters' in query && Array.isArray(query.filters) ? query.filters : [],
    [query],
  );

  function findVal<T>(fieldName: string, op: FilterOperator): T | undefined {
    const match = queryFilters.find(
      ({ field, operator }) => field === fieldName && operator === op,
    );
    return match?.value ? (match.value as T) : undefined;
  }

  const constituentLabelIds = findVal<string[]>(
    ConstituentSearchFilterField.CONSTITUENT_LABEL_IDS,
    FilterOperator.ANY,
  );
  const contributionLabelIds = findVal<string[]>(
    ConstituentSearchFilterField.DONATION_LABEL_IDS,
    FilterOperator.ANY,
  );
  const connectionId = findVal<string>(
    ConstituentSearchFilterField.CONNECTION_CONSTITUENT_IDS,
    FilterOperator.IS,
  );

  const { data: customFields } = useCustomFields();
  const { data: constituentLabels } = usePreselectedLabels(constituentLabelIds);
  const { data: contributionLabels } =
    usePreselectedLabels(contributionLabelIds);
  const { data: constituentConnection } = useConstituent(connectionId);

  const defaultValues = React.useMemo(
    () => ({
      ...mergeDefaultValues(
        ConstituentSearchDefaultValues,
        queryFilters,
        customFields,
      ),
      [TemporaryFilterField.CONSTITUENT_LABELS]: constituentLabels || [],
      [TemporaryFilterField.CONNECTION_CONSTITUENT]: constituentConnection as
        | Constituent
        | undefined,
      [TemporaryFilterField.CONTRIBUTION_LABELS]: contributionLabels || [],
      ...(iWaveIntegration && IWaveFilterDefaultValues),
    }),
    [
      constituentConnection,
      constituentLabels,
      contributionLabels,
      customFields,
      iWaveIntegration,
      queryFilters,
    ],
  );
  const formMethods = useForm({ defaultValues });

  const { reset } = formMethods;

  React.useEffect(() => {
    reset(defaultValues);
  }, [defaultValues]);

  return <FormProvider {...formMethods}>{children}</FormProvider>;
}

export default ConstituentSearchFormProvider;
